import { apiFetch } from '../../lib/api/client.js';
import type { LaunchMode, LaunchMethod, LaunchResult } from './api.js';

export class PopupBlockedError extends Error {
  url: string;
  constructor(url: string) {
    super('Popup blocked');
    this.name = 'PopupBlockedError';
    this.url = url;
  }
}

/**
 * Result of launching a game.
 *
 * - `iframe` — caller must mount the iframe modal with these props; the modal
 *   builds the form (for POST) and submits into its own iframe.
 * - `redirect` — already navigated/submitted on the current tab; caller has
 *   nothing left to do (the page is being replaced).
 * - `popup` — already opened/navigated/submitted into a new tab; caller has
 *   nothing left to do.
 * - `internal` — in-house (Originals) game; caller navigates to the in-app
 *   route `/games/originals/<code>?session=<token>` (no external window/iframe).
 */
export type LaunchOutcome =
  | {
      kind: 'iframe';
      url: string;
      method: LaunchMethod;
      payload: Record<string, unknown> | null;
    }
  | { kind: 'redirect' }
  | { kind: 'popup' }
  | { kind: 'internal'; code: string; session: string };

export interface LaunchOptions {
  providerId: string;
  gameId: string;
  lang: string;
  /**
   * Provider-wide deep view (SUPERM frameless: my-tickets / results pages).
   * The backend still wants a gameId for provider resolution, but the
   * provider opens the view instead of that game's board.
   */
  path?: 'lottery-tickets' | 'lottery-results';
  /**
   * Per-provider hint from the catalog (`game.launchMode/launchMethod`).
   * When mode === 'popup' the function opens `about:blank` synchronously
   * inside the click handler so Brave/Chrome don't strip the user-gesture
   * flag during the `await` for /launch. Without the hint we still try, but
   * the popup may be blocked on the first click of a session.
   */
  hint?: { mode: LaunchMode; method: LaunchMethod } | null;
}

const POPUP_WINDOW_NAME = 'bf-game-launch';

export async function launchGame(opts: LaunchOptions): Promise<LaunchOutcome> {
  const { providerId, gameId, lang, hint, path } = opts;

  // Synchronous popup pre-open — only when we already know mode === 'popup'.
  // For iframe/redirect we'd be churning a blank tab the user never asked
  // for, so we wait until the response.
  let preOpened: Window | null = null;
  if (hint?.mode === 'popup') {
    preOpened = window.open('about:blank', POPUP_WINDOW_NAME);
    if (preOpened) {
      try {
        preOpened.opener = null;
      } catch {
        /* same-origin assignment usually succeeds; harmless if it doesn't */
      }
    }
  }

  let result: LaunchResult;
  try {
    // The storefront root, not the current page: the embed pages launch on
    // mount, so a provider's exit link back to them would reopen the game.
    const returnUrl = `${window.location.origin}/`;
    result = await apiFetch<LaunchResult>('/launch', {
      method: 'POST',
      body: JSON.stringify({ providerId, gameId, lang, returnUrl, ...(path ? { path } : {}) }),
      headers: { 'content-type': 'application/json' },
    });
  } catch (err) {
    closeIfOpen(preOpened);
    throw err;
  }

  switch (result.mode) {
    case 'iframe':
      closeIfOpen(preOpened);
      return {
        kind: 'iframe',
        url: result.url,
        method: result.method,
        payload: result.payload,
      };

    case 'redirect':
      closeIfOpen(preOpened);
      if (result.method === 'POST') {
        submitForm(result.url, result.payload ?? {}, '_self');
      } else {
        window.location.assign(result.url);
      }
      return { kind: 'redirect' };

    case 'popup': {
      const win = preOpened ?? window.open('about:blank', POPUP_WINDOW_NAME);
      if (!win || win.closed) {
        throw new PopupBlockedError(result.url);
      }
      if (!preOpened) {
        try {
          win.opener = null;
        } catch {
          /* ignore */
        }
      }
      if (result.method === 'POST') {
        submitForm(result.url, result.payload ?? {}, POPUP_WINDOW_NAME);
      } else {
        win.location.replace(result.url);
      }
      return { kind: 'popup' };
    }

    case 'internal': {
      // result.url is an in-app relative route: /games/originals/<code>?session=<token>
      closeIfOpen(preOpened);
      const u = new URL(result.url, window.location.origin);
      const code = u.pathname.split('/').filter(Boolean).pop() ?? '';
      const session = u.searchParams.get('session') ?? '';
      // no external window/iframe for in-house games
      return { kind: 'internal', code, session };
    }
  }
}

function closeIfOpen(win: Window | null): void {
  if (win && !win.closed) {
    try {
      win.close();
    } catch {
      /* ignore */
    }
  }
}

/**
 * Build a hidden POST form and submit it. `target` chooses the destination
 * window: '_self' for the current tab, a window/iframe name for a popup or
 * embedded frame.
 */
export function submitForm(
  url: string,
  payload: Record<string, unknown>,
  target: string,
): void {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = url;
  form.target = target;
  form.style.display = 'none';
  form.enctype = 'application/x-www-form-urlencoded';
  for (const [k, v] of Object.entries(payload)) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = k;
    input.value = typeof v === 'string' ? v : JSON.stringify(v);
    form.appendChild(input);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
}
